import { useEffect, useRef, useState } from 'react'
import { ClipboardPaste, Eye, Plus, Trash2, TriangleAlert } from 'lucide-react'

import { Button } from '../../ui/Button'
import { Textarea } from '../../ui/Controls'
import { Input } from '../../ui/Field'
import { IconButton } from '../../ui/IconButton'
import { looksSecret, parseEnvFile } from '../../core/envFile'
import type { GuidedEnvironment } from '../../core/guided'

const PASTE_MAX_BYTES = 32 * 1024
const PASTE_MAX_LINES = 500
const STATUS_MS = 5000

/**
 * Merge parsed entries into the rows: an existing key (exact match) takes the
 * new value in place, and a new key fills the first completely empty row
 * before it is appended.
 */
export function mergeEnv(env: GuidedEnvironment[], entries: GuidedEnvironment[]): { env: GuidedEnvironment[]; added: number; updated: number } {
  const next = [...env]
  let added = 0
  let updated = 0
  for (const entry of entries) {
    const existing = next.findIndex((row) => row.key.trim() === entry.key)
    if (existing >= 0) {
      next[existing] = { key: entry.key, value: entry.value }
      updated += 1
      continue
    }
    const empty = next.findIndex((row) => row.key.trim() === '' && row.value === '')
    if (empty >= 0) next[empty] = entry
    else next.push(entry)
    added += 1
  }
  return { env: next, added, updated }
}

export function pasteSummary(added: number, updated: number, skippedLines: number[]): string {
  const parts = [
    added > 0 ? `added ${added}` : '',
    updated > 0 ? `updated ${updated}` : '',
    skippedLines.length > 0 ? `skipped ${skippedLines.length} line${skippedLines.length === 1 ? '' : 's'} (${skippedLines.join(', ')})` : '',
  ].filter(Boolean).join(' · ')
  return parts.charAt(0).toUpperCase() + parts.slice(1)
}

/** The "Paste .env" action for the Environment card header. */
export function EnvPasteButton({ open, onOpen }: { open: boolean; onOpen: () => void }) {
  return <Button id="env-paste-open" type="button" variant="ghost" size="sm" aria-expanded={open} aria-controls={open ? 'env-paste-panel' : undefined} onClick={onOpen}><ClipboardPaste aria-hidden="true" size={16} />Paste .env</Button>
}

/**
 * The Environment card body. Pasted text lives only in this component's state
 * and is cleared after a merge: it is never persisted as raw text.
 */
export function EnvList({ env, errors, submitted, pasteOpen, onPasteOpenChange, maskedKeys, onChange }: { env: GuidedEnvironment[]; errors: Record<string, string>; submitted: boolean; pasteOpen: boolean; onPasteOpenChange: (open: boolean) => void; maskedKeys?: readonly string[] | undefined; onChange: (env: GuidedEnvironment[]) => void }) {
  // Secret rows another section wrote (the S3 connection) are masked here; that section reveals them.
  const masked = new Set(maskedKeys ?? [])
  const [pasteText, setPasteText] = useState('')
  const [pasteError, setPasteError] = useState<string | null>(null)
  const [status, setStatus] = useState('')
  const pendingFocus = useRef<string | null>(null)
  const wasOpen = useRef(pasteOpen)

  useEffect(() => {
    if (!pendingFocus.current) return
    document.getElementById(pendingFocus.current)?.focus()
    pendingFocus.current = null
  })

  useEffect(() => {
    if (pasteOpen && !wasOpen.current) document.getElementById('env-paste-text')?.focus()
    if (!pasteOpen && wasOpen.current) document.getElementById('env-paste-open')?.focus()
    wasOpen.current = pasteOpen
  }, [pasteOpen])

  useEffect(() => {
    if (!status) return
    const timer = window.setTimeout(() => setStatus(''), STATUS_MS)
    return () => window.clearTimeout(timer)
  }, [status])

  const setRow = (index: number, change: Partial<GuidedEnvironment>) => onChange(env.map((item, itemIndex) => itemIndex === index ? { ...item, ...change } : item))
  const remove = (index: number) => {
    const next = env.filter((_, itemIndex) => itemIndex !== index)
    pendingFocus.current = next.length > 0 ? `env-key-${Math.min(index, next.length - 1)}` : 'env-add'
    onChange(next)
  }
  const add = () => {
    pendingFocus.current = `env-key-${env.length}`
    onChange([...env, { key: '', value: '' }])
  }
  const closePaste = () => {
    setPasteText('')
    setPasteError(null)
    onPasteOpenChange(false)
  }
  const applyPaste = () => {
    // A trailing newline ends the last line; it does not start a new one.
    const lineCount = pasteText.replace(/\r?\n$/, '').split(/\r?\n/).length
    if (new TextEncoder().encode(pasteText).length > PASTE_MAX_BYTES || lineCount > PASTE_MAX_LINES) {
      setPasteError("That is too long to paste here. Use the SDL editor for large configurations.")
      return
    }
    const parsed = parseEnvFile(pasteText)
    if (parsed.entries.length === 0) {
      setPasteError('No KEY=value lines found.')
      return
    }
    const merged = mergeEnv(env, parsed.entries)
    onChange(merged.env)
    setStatus(pasteSummary(merged.added, merged.updated, parsed.skippedLines))
    closePaste()
  }

  return <>
    <div className="env-header-lines">
      <p className="field-help row-list-notice"><Eye aria-hidden="true" size={16} /><span>Values are stored in the deployment manifest as plain text and are visible to the host. Do not put passwords or API keys here.</span></p>
      <p className="field-help" role="status">{status}</p>
    </div>
    {pasteOpen && <div className="env-paste-panel" id="env-paste-panel">
      <Textarea id="env-paste-text" label="Paste .env contents" rows={5} spellCheck={false} autoComplete="off" placeholder={'DEVICE=cuda\nLOG_LEVEL=info'} value={pasteText} {...(pasteError ? { error: pasteError } : {})} onChange={(event) => { setPasteText(event.target.value); setPasteError(null) }} />
      <div className="row-list-actions">
        <Button type="button" variant="secondary" size="sm" onClick={applyPaste}>Add variables</Button>
        <Button type="button" variant="ghost" size="sm" onClick={closePaste}>Cancel</Button>
      </div>
    </div>}
    {env.length === 0
      ? <p className="field-help row-list-empty">No variables. Your image's defaults apply.</p>
      : <div className="row-list row-list--env" role="group" aria-label="Environment variables">
        <div className="row-list-head" aria-hidden="true"><span>Key</span><span /><span>Value</span><span /></div>
        {env.map((row, index) => {
          const secret = looksSecret(row.key)
          return <div className="row-list-row" key={index}>
            {/* No auto-uppercase: the key rule accepts lowercase and some images read it (decision D1). */}
            <Input id={`env-key-${index}`} className="row-list-mono" label="Key" hideLabel autoCapitalize="characters" spellCheck={false} autoComplete="off" placeholder="KEY" value={row.key} error={submitted ? errors[`env-${index}`] : undefined} onChange={(event) => setRow(index, { key: event.target.value })} />
            <span className="row-list-equals" aria-hidden="true">=</span>
            <Input id={`env-value-${index}`} className="row-list-mono" label="Value" hideLabel spellCheck={false} autoComplete="off" {...(secret && masked.has(row.key.trim()) ? { type: 'password' } : {})} placeholder="value" value={row.value} error={submitted ? errors[`envvalue-${index}`] : undefined} aria-describedby={secret ? `env-secret-${index}` : undefined} onChange={(event) => setRow(index, { value: event.target.value })} />
            <IconButton className="row-list-remove" label={row.key.trim() ? `Remove ${row.key.trim()}` : 'Remove empty variable'} onClick={() => remove(index)}><Trash2 aria-hidden="true" size={16} /></IconButton>
            {secret && <p className="env-secret-hint" id={`env-secret-${index}`}><TriangleAlert aria-hidden="true" size={14} />Looks like a secret. The host can read it in plain text.</p>}
          </div>
        })}
      </div>}
    <div className="row-list-actions">
      <Button id="env-add" type="button" variant="secondary" size="sm" onClick={add}><Plus aria-hidden="true" size={16} />Add variable</Button>
    </div>
  </>
}
